import React, {memo} from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Grid from '@mui/material/Grid';
import Image from 'next/image';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import MUIContainer from '@/components/MUI-Components/MUI-Container';
import {colors, fontSize, fontWeight, borderRadius, spacing, lineHeightRatio, contentWidth} from '@/styles/tokens';
import {cdnUrl} from '@/util/cdnImage';
import {renderParts} from './richText';
import type {FeatureSectionData, FeatureBlock} from '@/util/contents/drawpost-data';

interface FeatureSectionProps {
    heading: string;
    content: FeatureSectionData;
    /** Which side the screenshot sits on (defaults to right). */
    imagePosition?: 'left' | 'right';
    /** Section background — alternate between sections (defaults to default). */
    background?: 'default' | 'subtle';
}

const badgeSx = {
    display: 'inline-flex',
    alignSelf: 'flex-start',
    alignItems: 'center',
    gap: spacing[8],
    px: spacing[16],
    py: spacing[8],
    borderRadius: borderRadius.full,
    bgcolor: colors.accent.indigoSubtle,
    color: colors.accent.indigo,
    fontSize: fontSize[16],
    fontWeight: fontWeight.semibold,
    mb: 1
} as const;

const badgeIconFilter =
    'brightness(0) saturate(100%) invert(20%) sepia(95%) saturate(6000%) hue-rotate(244deg) brightness(95%) contrast(101%)';

const headingSx = {
    color: colors.text.primary,
    fontSize: {xs: fontSize[32], sm: fontSize[48]},
    lineHeight: lineHeightRatio.snug,
    mb: 3
} as const;

const bodySx = {
    color: colors.text.primary,
    fontSize: fontSize[16],
    fontWeight: fontWeight.regular,
    lineHeight: lineHeightRatio.looser,
    mb: 2
} as const;

const bulletListSx = {
    listStyle: 'none',
    p: 0,
    m: 0,
    mb: 1
} as const;

const bulletItemSx = {
    display: 'flex',
    alignItems: 'flex-start',
    gap: spacing[8],
    mb: 1.5,
    color: colors.text.primary,
    fontSize: fontSize[16],
    lineHeight: lineHeightRatio.looser
} as const;

const checkSpanSx = {
    color: colors.accent.indigo,
    flexShrink: 0,
    mt: spacing[4],
    fontSize: fontSize[16],
    fontWeight: fontWeight.bold
} as const;

const checkIconItemSx = {
    display: 'flex',
    alignItems: 'flex-start',
    gap: spacing[8],
    mb: 2
} as const;

const checkIconSx = {
    color: colors.accent.indigo,
    fontSize: fontSize[20],
    flexShrink: 0,
    mt: spacing[4]
} as const;

const checkIconTextSx = {
    color: colors.text.primary,
    fontSize: fontSize[16],
    fontWeight: fontWeight.regular,
    lineHeight: lineHeightRatio.loose
} as const;

const textColSx = {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center'
} as const;

const imageColCenteredSx = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center'
} as const;

const boldPartSx = {fontWeight: fontWeight.semibold, color: colors.text.primary} as const;

function renderBlock(block: FeatureBlock, index: number) {
    if (block.type === 'bullets') {
        if (block.variant === 'check-icon') {
            return (
                <Box key={index}>
                    {block.items.map((item) => (
                        <Box key={item} sx={checkIconItemSx}>
                            <CheckCircleIcon sx={checkIconSx} />
                            <Typography sx={checkIconTextSx} className="font-regular-text remove-underline">
                                {item}
                            </Typography>
                        </Box>
                    ))}
                </Box>
            );
        }
        return (
            <Box key={index} component="ul" sx={bulletListSx}>
                {block.items.map((item) => (
                    <Box component="li" key={item} sx={bulletItemSx}>
                        <Box component="span" sx={checkSpanSx}>
                            ✓
                        </Box>
                        <span>{item}</span>
                    </Box>
                ))}
            </Box>
        );
    }

    return (
        <Typography key={index} sx={bodySx} className="font-regular-text remove-underline">
            {renderParts(block.parts, {boldSx: boldPartSx})}
        </Typography>
    );
}

const FeatureSection = memo(function FeatureSection({
    heading,
    content,
    imagePosition = 'right',
    background = 'default'
}: FeatureSectionProps) {
    const {image} = content;
    const isFill = Boolean(image.aspectRatio);

    const screenshotSx = {
        position: 'relative',
        overflow: 'hidden',
        width: '100%',
        maxWidth: contentWidth.screenshot,
        mx: 'auto',
        ...(image.aspectRatio ? {aspectRatio: image.aspectRatio} : {}),
        ...(image.surface ? {bgcolor: colors.background.subtle, borderRadius: borderRadius.xl} : {})
    } as const;

    const sectionSx = {
        bgcolor: background === 'subtle' ? colors.background.subtle : colors.background.default
    } as const;

    const textCol = (
        <Grid item xs={12} md={6} sx={textColSx}>
            <Box sx={badgeSx}>
                <Image
                    src={cdnUrl(content.badgeIconSrc)}
                    alt={content.badgeIconAlt}
                    aria-hidden="true"
                    width={16}
                    height={16}
                    style={{filter: badgeIconFilter}}
                />
                <span>{content.badgeLabel}</span>
            </Box>

            <Typography component="h2" sx={headingSx} className="font-headers">
                {heading}
            </Typography>

            {content.blocks.map(renderBlock)}
        </Grid>
    );

    const imageCol = (
        <Grid item xs={12} md={6} sx={imageColCenteredSx}>
            <Box sx={screenshotSx}>
                {isFill ? (
                    <Image
                        src={cdnUrl(image.path)}
                        alt={image.alt}
                        fill
                        quality={90}
                        loading="lazy"
                        style={{objectFit: 'cover'}}
                        sizes="(max-width: 900px) 100vw, 50vw"
                    />
                ) : (
                    <Image
                        src={cdnUrl(image.path)}
                        alt={image.alt}
                        width={image.width}
                        height={image.height}
                        quality={90}
                        loading="lazy"
                        style={{width: '100%', height: 'auto', display: 'block'}}
                        sizes="(max-width: 900px) 100vw, 650px"
                    />
                )}
            </Box>
        </Grid>
    );

    return (
        <Box className="top-space bottom-spacing mobile-top-padding mobile-padding-bottom" sx={sectionSx}>
            <MUIContainer>
                <Grid container spacing={{xs: 4, md: 8}} alignItems="center">
                    {imagePosition === 'left' ? (
                        <>
                            {imageCol}
                            {textCol}
                        </>
                    ) : (
                        <>
                            {textCol}
                            {imageCol}
                        </>
                    )}
                </Grid>
            </MUIContainer>
        </Box>
    );
});

export default FeatureSection;
